// The shape of a bracket: seed order, round names and the national
// tournament's regions. Shared so the national tournament page can draw
// the empty bracket before Selection Day exactly as the real one will be.

export const REGION_NAMES = ["North", "South", "East", "West"];

/**
 * Seeds in bracket order, so neighbours meet in round 1 and the top seeds
 * meet as late as possible. standardOrder(8) -> [1, 8, 4, 5, 2, 7, 3, 6].
 * @param {number} size a power of 2
 * @return {number[]}
 */
export function standardOrder(size) {
  let order = [1];
  while (order.length < size) {
    const n = order.length * 2;
    order = order.flatMap((s) => [s, n + 1 - s]);
  }
  return order;
}

/**
 * @param {string} style "conference", "national" or "playoffs"
 * @param {number} teams teams in the round
 * @param {boolean} first
 * @return {string}
 */
export function roundName(style, teams, first) {
  if (style === "national") {
    const names = {2: "Championship", 4: "Final Four", 8: "Elite Eight",
      16: "Sweet 16"};
    if (names[teams]) return names[teams];
    return first ? "First Round" : "Second Round";
  }
  if (teams === 2) return style === "playoffs" ? "Finals" : "Championship";
  if (teams === 4) return "Semifinals";
  if (teams === 8) return "Quarterfinals";
  return first ? "First Round" : `Round of ${teams}`;
}

/**
 * Regions of the national tournament: four from 16 teams up, else one.
 * @param {number} fieldSize
 * @return {number}
 */
export function nationalRegionCount(fieldSize) {
  return fieldSize >= 16 ? 4 : 1;
}

/**
 * The order regions fill the bracket: the overall 1 seed's region (0)
 * meets the 4th (3) in the Final Four.
 * @param {number} regionCount
 * @return {number[]}
 */
export function nationalRegionOrder(regionCount) {
  return regionCount === 4 ? [0, 3, 1, 2] : [0];
}

/**
 * The national tournament bracket before the field is picked: every
 * first-round slot has its seed and region but no team. Same shape as
 * nationalBracket() in src/league/selection.js, with `empty: true`.
 * @param {number} fieldSize a power of 2
 * @return {object} bracket
 */
export function emptyNationalBracket(fieldSize) {
  const regionCount = nationalRegionCount(fieldSize);
  const order = nationalRegionOrder(regionCount);
  const perRegion = fieldSize / regionCount;
  const slots = order.flatMap((region) => standardOrder(perRegion)
      .map((seed) => ({team: null, seed, region})));
  const rounds = [];
  for (let r = 0; (fieldSize >> r) > 1; r++) {
    const count = fieldSize >> (r + 1);
    rounds.push({
      name: roundName("national", count * 2, r === 0),
      matchups: Array.from({length: count}, (_, index) => ({
        round: r, index, bestOf: 1,
        top: r === 0 ? slots[2 * index] : null,
        bottom: r === 0 ? slots[2 * index + 1] : null,
        games: [], wins: {top: 0, bottom: 0}, winner: null, pending: null,
      })),
    });
  }
  return {id: "national", name: "National Tournament", style: "national",
    rounds, champion: null, empty: true, regionOrder: order,
    regionNames: regionCount === 4 ? REGION_NAMES : ["National"]};
}
